import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import {
    AlertCircle,
    BarChart3,
    Calendar,
    Download,
    Globe,
    PieChart,
    RefreshCw,
    TrendingDown,
    TrendingUp,
    Users
} from 'lucide-react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart as RechartsPieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {useSocket} from "./SocketContext.jsx";
//import { fetchSites, fetchSiteHistory, fetchGlobalStats } from '../store/slices/sitesSlice';
import {fetchGlobalStats, fetchSiteHistory, fetchSites} from '../store/features/sites/sitesSlice';
import {api} from "../services/api.js";


// Couleurs pour les graphiques - DÉPLACÉ EN HAUT
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

export default function SiteCharts() {
    const dispatch = useDispatch();
    const sites = useSelector(state => state.sites.items);
    const sitesLoading = useSelector(state => state.sites.loading);
    const sitesHistory = useSelector(state => state.sites.history);
    const globalStats = useSelector(state => state.sites.globalStats);
    const realtimeStats = useSelector(state => state.sites.realtimeStats);
    const { socketService, getVisitHistory, realtimeStats: socketRealtimeStats } = useSocket();

    const [timeRange, setTimeRange] = useState('day');
    const [selectedSite, setSelectedSite] = useState('all');
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [visitTrends, setVisitTrends] = useState({});
    const [filteredSites, setFilteredSites] = useState([]);

    // Références pour éviter les rechargements inutiles
    const lastFetchRef = useRef(null);
    const prevRealtimeStatsRef = useRef({});

    // Charger les sites au montage (une seule fois)
    useEffect(() => {
        if (sites.length === 0) {
            dispatch(fetchSites());
        }
    }, [dispatch]);

    // Charger les statistiques globales (une seule fois)
    useEffect(() => {
        if (!globalStats) {
            dispatch(fetchGlobalStats());
        }
    }, [dispatch]);

    // Protection contre les données invalides
    const safeSites = useMemo(() => {
        const baseSites = _.isArray(sites) ? sites : _.isObject(sites) ? _.values(sites) : [];

        // Ajouter les sites temporaires depuis realtimeStats
        const tempSites = _.keys(socketRealtimeStats)
            .filter(siteKey => !baseSites.some(site =>
                site.siteKey === siteKey || site.id === siteKey
            ))
            .map(siteKey => ({
                id: siteKey,
                siteKey: siteKey,
                name: siteKey,
                url: `https://${siteKey}`,
                status: 'active',
                state: 'online',
                totalVisites: socketRealtimeStats[siteKey]?.total || 0,
                todayVisites: socketRealtimeStats[siteKey]?.today || 0,
                onlineVisitors: socketRealtimeStats[siteKey]?.online || 0,
                peakVisitors: socketRealtimeStats[siteKey]?.peak || 0,
                isTemp: true,
                metadata: {
                    uptime: 100,
                    responseTime: 50
                }
            }));

        return [...baseSites, ...tempSites];
    }, [sites, socketRealtimeStats]);

    // Mettre à jour les sites filtrés
    useEffect(() => {
        setFilteredSites(safeSites);
    }, [safeSites]);

    // Charger l'historique des visites seulement quand le site sélectionné change
    const loadVisitHistory = useCallback(async () => {
        if (selectedSite !== 'all') {
            try {
                dispatch(fetchSiteHistory(selectedSite));
            } catch (error) {
                console.warn('Impossible de charger l\'historique:', error);
            }
        }
    }, [selectedSite, dispatch]);

    // Récupérer l'historique depuis le store seulement quand nécessaire
    useEffect(() => {
        if (selectedSite !== 'all') {
            if (sitesHistory[selectedSite]) {
                setHistoryData(sitesHistory[selectedSite] || []);
            } else {
                loadVisitHistory();
            }
        } else {
            setHistoryData([]);
        }
    }, [selectedSite, sitesHistory[selectedSite]]); // Seulement quand ces valeurs changent

    // Vérifier si les stats ont changé significativement
    const hasRealtimeStatsChanged = useCallback((currentStats, prevStats) => {
        if (!currentStats || !prevStats) return true;

        const currentKeys = Object.keys(currentStats);
        const prevKeys = Object.keys(prevStats);

        if (currentKeys.length !== prevKeys.length) return true;

        for (const key of currentKeys) {
            const current = currentStats[key];
            const prev = prevStats[key];

            if (!prev) return true;

            // Vérifier les changements significatifs
            if (current.online !== prev.online) return true;
            if (current.today !== prev.today) return true;
            if (current.total !== prev.total) return true;
        }

        return false;
    }, []);

    // Données réelles depuis l'API avec contrôle de fréquence
    const fetchRealData = useCallback(async (force = false) => {
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000; // 5 minutes

        // Éviter les appels trop fréquents
        if (!force && lastFetchRef.current && (now - lastFetchRef.current < fiveMinutes)) {
            console.log('⏸️ Fetch skip - trop récent');
            return;
        }

        setLoading(true);
        lastFetchRef.current = now;

        try {
            const params = {
                range: timeRange,
                siteId: selectedSite === 'all' ? 'all' : selectedSite
            };

            const response = await api.get('/sites/charts/data', { params });
            let realData = response.data;

            // Si pas de données, utiliser des données de secours
            if (!realData || realData.length === 0) {
                realData = generateFallbackData();
            }

            setChartData(realData);

            // Calculer les tendances
            if (realData.length > 1) {
                const firstValue = realData[0]?.visites || 0;
                const lastValue = realData[realData.length - 1]?.visites || 0;
                const trendPercentage = firstValue > 0 ?
                    ((lastValue - firstValue) / firstValue * 100).toFixed(1) : 0;

                setVisitTrends({
                    percentage: trendPercentage,
                    isPositive: trendPercentage > 0,
                    current: lastValue,
                    previous: firstValue
                });
            }

        } catch (error) {
            console.error('Erreur chargement données graphiques:', error);
            // Données de secours garanties
            const fallbackData = generateFallbackData();
            setChartData(fallbackData);
        } finally {
            setLoading(false);
        }
    }, [timeRange, selectedSite, safeSites]);

    // Générer des données de secours
    const generateFallbackData = useCallback(() => {
        if (selectedSite === 'all') {
            // Agréger les données de tous les sites
            const totalSites = safeSites;
            const todayVisits = _.sumBy(totalSites, site =>
                _.toNumber(_.get(site, 'todayVisites', 0))
            );

            if (timeRange === 'day') {
                return Array.from({ length: 24 }, (_, hour) => ({
                    name: `${hour.toString().padStart(2, '0')}h`,
                    visites: Math.max(1, Math.round(todayVisits * (0.05 + Math.random() * 0.1))),
                    online: Math.round(_.sumBy(totalSites, site =>
                        _.toNumber(_.get(site, 'onlineVisitors', 0))
                    ) * (hour >= 8 && hour <= 22 ? 0.7 : 0.3))
                }));
            } else if (timeRange === 'week') {
                const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
                const totalVisits = _.sumBy(totalSites, site =>
                    _.toNumber(_.get(site, 'totalVisites', 0))
                );
                const avgDailyVisits = totalVisits > 0 ? totalVisits / 30 : 50;

                return days.map((day, index) => ({
                    name: day,
                    visites: Math.round(avgDailyVisits * (0.8 + Math.random() * 0.4)),
                    online: Math.round(avgDailyVisits * 0.1 * (0.8 + Math.random() * 0.4))
                }));
            } else {
                return Array.from({ length: 4 }, (_, week) => ({
                    name: `Sem ${week + 1}`,
                    visites: Math.round(200 * (0.8 + Math.random() * 0.4)),
                    online: Math.round(10 * (0.8 + Math.random() * 0.4))
                }));
            }
        } else {
            // Données pour un site spécifique
            return Array.from({ length: timeRange === 'day' ? 12 : timeRange === 'week' ? 7 : 4 }, (_, i) => ({
                name: timeRange === 'day' ? `${i * 2}h` :
                    timeRange === 'week' ? ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'][i] :
                        `Sem ${i + 1}`,
                visites: Math.round(20 * (0.8 + Math.random() * 0.4)),
                online: Math.round(5 * (0.8 + Math.random() * 0.4))
            }));
        }
    }, [selectedSite, timeRange, safeSites]);

    // Générer les données du graphique SEULEMENT quand timeRange ou selectedSite change
    useEffect(() => {
        console.log('📊 Chargement des données graphiques...');
        fetchRealData(true); // Force le chargement initial
    }, [timeRange, selectedSite]); // Seulement ces dépendances

    // Écouter les nouvelles visites pour rafraîchir seulement quand nécessaire
    useEffect(() => {
        // Vérifier si les stats ont changé
        const hasChanged = hasRealtimeStatsChanged(socketRealtimeStats, prevRealtimeStatsRef.current);

        if (hasChanged) {
            console.log('🔄 Nouvelles données socket détectées');
            prevRealtimeStatsRef.current = socketRealtimeStats;

            // Rafraîchir les données seulement si c'est significatif
            fetchRealData();
        }
    }, [socketRealtimeStats, fetchRealData, hasRealtimeStatsChanged]);

    // Calculer les statistiques des sites avec lodash
    const siteStats = useMemo(() => {
        const safeRealtimeStats = _.isObject(socketRealtimeStats) ? socketRealtimeStats : {};

        // Debug: vérifier les stats du site de test


        const currentSites = safeSites;

        // Calculer les statistiques de base avec protection
        const totalVisits = _.sumBy(currentSites, site => _.toNumber(_.get(site, 'totalVisites', 0)));
        const todayVisits = _.sumBy(currentSites, site => _.toNumber(_.get(site, 'todayVisites', 0)));
        const activeSites = _.filter(currentSites, site => _.get(site, 'status') === 'active').length;
        const onlineSites = _.filter(currentSites, site => _.get(site, 'state') === 'online' || _.get(site, 'state') === true).length;

        // Calculer les visiteurs en ligne depuis realtimeStats avec protection
        const onlineNow = _.reduce(
            _.values(safeRealtimeStats),
            (sum, stat) => {
                if (_.isObject(stat)) {
                    const online = _.toNumber(_.get(stat, 'online', 0));
                    return _.isNaN(online) ? sum : sum + online;
                }
                return sum;
            },
            0
        );

        // Calculer le pic simultané avec protection
        const peakVisitors = _.max(
            _.map(_.values(safeRealtimeStats), s => {
                const peak = _.toNumber(_.get(s, 'peak', 0));
                return _.isNaN(peak) ? 0 : peak;
            })
        ) || 0;

        // Utiliser les statistiques globales si disponibles
        const globalStatsData = globalStats || {};

        // Calculer l'évolution des visites
        const yesterdayVisits = todayVisits * 0.8; // Simulation ou utiliser les données globales
        const visitChange = globalStatsData.trends?.daily ||
            (yesterdayVisits > 0 ? ((todayVisits - yesterdayVisits) / yesterdayVisits * 100).toFixed(1) : 0);

        // Données pour le graphique en secteurs avec protection
        const pieData = _.chain(currentSites)
            .filter(site => _.toNumber(_.get(site, 'totalVisites', 0)) > 0)
            .sortBy(site => -_.toNumber(_.get(site, 'totalVisites', 0)))
            .take(6)
            .map((site, index) => {
                const total = _.toNumber(_.get(site, 'totalVisites', 0));
                const name = _.get(site, 'name', 'Site inconnu');

                return {
                    name: _.truncate(name, { length: 12 }),
                    value: _.isNaN(total) ? 0 : total,
                    siteKey: _.get(site, 'siteKey') || _.get(site, 'id', ''),
                    today: _.toNumber(_.get(site, 'todayVisites', 0)),
                    color: COLORS[index % COLORS.length],
                    status: _.get(site, 'status', 'inactive')
                };
            })
            .value();

        // Top 5 des sites les plus visités
        const topSites = _.chain(currentSites)
            .filter(site => _.toNumber(_.get(site, 'totalVisites', 0)) > 0)
            .sortBy(site => -_.toNumber(_.get(site, 'totalVisites', 0)))
            .take(5)
            .map(site => ({
                id: _.get(site, 'id'),
                name: _.get(site, 'name', 'Site inconnu'),
                url: _.get(site, 'url', ''),
                visits: _.toNumber(_.get(site, 'totalVisites', 0)),
                today: _.toNumber(_.get(site, 'todayVisites', 0)),
                online: _.toNumber(_.get(site, 'onlineVisitors', 0)),
                status: _.get(site, 'status', 'inactive'),
                state: _.get(site, 'state', false),
                uptime: _.toNumber(_.get(site, 'metadata.uptime', _.get(site, 'uptime', 99.5))),
                responseTime: _.toNumber(_.get(site, 'metadata.responseTime', _.get(site, 'responseTime', 150)))
            }))
            .value();

        // Statistiques de performance
        const avgUptime = currentSites.length > 0 ?
            _.meanBy(currentSites, site => {
                const uptime = _.toNumber(_.get(site, 'metadata.uptime', _.get(site, 'uptime', 99.5)));
                return _.isNaN(uptime) ? 99.5 : uptime;
            }) : 99.5;

        const avgResponseTime = currentSites.length > 0 ?
            _.meanBy(currentSites, site => {
                const responseTime = _.toNumber(_.get(site, 'metadata.responseTime', _.get(site, 'responseTime', 150)));
                return _.isNaN(responseTime) ? 150 : responseTime;
            }) : 150;

        return {
            totalVisits: _.isNaN(totalVisits) ? 0 : totalVisits,
            todayVisits: _.isNaN(todayVisits) ? 0 : todayVisits,
            activeSites,
            onlineSites,
            onlineNow,
            peakVisitors,
            visitChange: parseFloat(visitChange) || 0,
            isVisitPositive: parseFloat(visitChange) > 0,
            pieData,
            topSites,
            avgUptime: _.isNaN(avgUptime) ? 99.5 : avgUptime,
            avgResponseTime: _.isNaN(avgResponseTime) ? 150 : avgResponseTime,
            totalSites: currentSites.length,
            globalStats: globalStatsData
        };
    }, [safeSites, socketRealtimeStats, globalStats]);

    // Formater les nombres
    const formatNumber = useCallback((num) => {
        const number = _.toNumber(num);
        if (_.isNaN(number)) return '0';

        if (number >= 1000000) return (number / 1000000).toFixed(1) + 'M';
        if (number >= 1000) return (number / 1000).toFixed(1) + 'k';
        return number.toLocaleString();
    }, []);

    // Outil personnalisé pour le graphique
    const CustomTooltip = useCallback(({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                    <p className="font-medium text-gray-900 mb-2">{label}</p>
                    {payload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div
                                    className="w-3 h-3 rounded-full mr-2"
                                    style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-sm text-gray-600">{entry.name}:</span>
                            </div>
                            <span className="text-sm font-medium ml-2">
                                {formatNumber(entry.value)}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    }, [formatNumber]);

    // Formater la date pour l'affichage
    const formatTimeRange = useCallback(() => {
        const now = new Date();
        switch(timeRange) {
            case 'day':
                return now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
            case 'week':
                const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 1));
                const endOfWeek = new Date(now.setDate(startOfWeek.getDate() + 6));
                return `Semaine du ${startOfWeek.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au ${endOfWeek.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
            case 'month':
                return now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
            default:
                return '';
        }
    }, [timeRange]);

    // Fonction pour rafraîchir toutes les données (manuelle)
    const handleRefresh = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([
                dispatch(fetchSites()),
                dispatch(fetchGlobalStats()),
                fetchRealData(true) // Force le rafraîchissement
            ]);
        } catch (error) {
            console.error('Erreur lors du rafraîchissement:', error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, fetchRealData]);

    // Gérer le changement de site
    const handleSiteChange = useCallback((e) => {
        const value = e.target.value;
        setSelectedSite(value);

        // Filtrer les sites si nécessaire
        if (value === 'active') {
            setFilteredSites(safeSites.filter(site => site.status === 'active'));
        } else if (value === 'online') {
            setFilteredSites(safeSites.filter(site => site.state === true || site.state === 'online'));
        } else {
            setFilteredSites(safeSites);
        }
    }, [safeSites]);

    // Bouton de test pour le site local
    const handleTestLocalSite = useCallback(async () => {
        console.log('🧪 Test du site local...');
        try {
            // Forcer le join via socket
            if (socketService && socketService.joinSite) {
                await socketService.joinSite('test-site-local');
                console.log('✅ Site de test rejoint');

                // Attendre un peu avant de rafraîchir
                setTimeout(() => {
                    handleRefresh();
                }, 1000);
            }
        } catch (error) {
            console.error('❌ Erreur test site:', error);
        }
    }, [socketService, handleRefresh]);

    // Si chargement initial, afficher un spinner
    if (sitesLoading && safeSites.length === 0) {
        return (
            <div className="flex items-center justify-center h-96">
                <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mr-3" />
                <span className="text-gray-600">Chargement des sites...</span>
            </div>
        );
    }

    // Debug: afficher les sites disponibles seulement en dev
    // useEffect(() => {
    //     if (process.env.NODE_ENV === 'development') {
    //         console.log('🔍 Sites disponibles:', safeSites.length);
    //         console.log('🔍 Stats temps réel:', socketRealtimeStats);
    //
    //         const testSite = safeSites.find(site =>
    //             site.siteKey === 'test-site-local' ||
    //             site.id === 'test-site-local'
    //         );
    //         if (testSite) {
    //             console.log('🔍 Site de test trouvé:', testSite);
    //         }
    //     }
    // }, [safeSites, socketRealtimeStats]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Graphique principal */}
            <div className="bg-white rounded-xl shadow p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <BarChart3 className="h-5 w-5 mr-2" />
                            Trafic en temps réel
                        </h3>
                        <p className="text-sm text-gray-500">
                            {timeRange === 'day' ? 'Aujourd\'hui' : timeRange === 'week' ? 'Cette semaine' : 'Ce mois'}
                            {selectedSite !== 'all' && ' • Site sélectionné'}
                        </p>
                    </div>

                    <div className="flex items-center space-x-3">
                        <div className="flex items-center border border-gray-300 rounded-lg">
                            <button
                                onClick={() => setTimeRange('day')}
                                className={`px-3 py-1.5 text-sm ${timeRange === 'day' ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}
                            >
                                Jour
                            </button>
                            <button
                                onClick={() => setTimeRange('week')}
                                className={`px-3 py-1.5 text-sm ${timeRange === 'week' ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}
                            >
                                Semaine
                            </button>
                            <button
                                onClick={() => setTimeRange('month')}
                                className={`px-3 py-1.5 text-sm ${timeRange === 'month' ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}
                            >
                                Mois
                            </button>
                        </div>

                        <select
                            value={selectedSite}
                            onChange={handleSiteChange}
                            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none min-w-[150px]"
                        >
                            <option value="all">Tous les sites</option>
                            <option value="active">Sites actifs seulement</option>
                            <option value="online">Sites en ligne seulement</option>
                            {_.map(safeSites, site => (
                                <option key={_.get(site, 'id')} value={_.get(site, 'siteKey') || _.get(site, 'id')}>
                                    {_.truncate(_.get(site, 'name', 'Site inconnu'), { length: 20 })}
                                </option>
                            ))}
                        </select>

                        <button
                            onClick={handleTestLocalSite}
                            className="px-3 py-1.5 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600"
                        >
                            🧪 Test Local
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="h-64 flex flex-col items-center justify-center">
                        <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mb-3" />
                        <p className="text-gray-500 text-sm">Chargement des données...</p>
                    </div>
                ) : chartData.length === 0 ? (
                    <div className="h-64 flex flex-col items-center justify-center text-gray-500">
                        <AlertCircle className="h-12 w-12 mb-4 opacity-50" />
                        <p className="text-lg font-medium mb-2">Aucune donnée disponible</p>
                        <p className="text-sm">Les données apparaîtront avec l'activité des sites</p>
                    </div>
                ) : (
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="name"
                                    tick={{ fontSize: 12 }}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                />
                                <YAxis
                                    tick={{ fontSize: 12 }}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                    tickFormatter={formatNumber}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="visites"
                                    name="Visites"
                                    stroke="#3b82f6"
                                    fill="url(#colorVisites)"
                                    fillOpacity={0.3}
                                    strokeWidth={2}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="online"
                                    name="En ligne"
                                    stroke="#10b981"
                                    fill="#10b981"
                                    fillOpacity={0.1}
                                    strokeWidth={1}
                                />
                                <defs>
                                    <linearGradient id="colorVisites" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                )}

                <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm text-gray-600">
                                <span className="font-medium text-gray-900">
                                    {formatNumber(siteStats.todayVisits)}
                                </span>
                                {' '}visites aujourd'hui
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                {formatTimeRange()}
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="text-sm">
                                {siteStats.isVisitPositive ? (
                                    <span className="text-green-600 flex items-center">
                                        <TrendingUp className="h-4 w-4 mr-1" />
                                        +{Math.abs(siteStats.visitChange)}% vs hier
                                    </span>
                                ) : (
                                    <span className="text-red-600 flex items-center">
                                        <TrendingDown className="h-4 w-4 mr-1" />
                                        {siteStats.visitChange}% vs hier
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={handleRefresh}
                                disabled={loading}
                                className="flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                            >
                                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                                Rafraîchir
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Statistiques et répartition */}
            <div className="space-y-6">
                {/* Stats en temps réel */}
                <div className="bg-white rounded-xl shadow p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Users className="h-5 w-5 mr-2" />
                            Vue d'ensemble
                        </h3>
                        <div className="flex items-center space-x-2">
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                {siteStats.onlineSites}/{siteStats.totalSites} en ligne
                            </span>
                            <button
                                onClick={handleRefresh}
                                className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                            >
                                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                                Actualiser
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-blue-700 mb-1">Visites totales</p>
                                    <p className="text-2xl font-bold text-blue-900">
                                        {formatNumber(siteStats.totalVisits)}
                                    </p>
                                </div>
                                <Users className="h-8 w-8 text-blue-600 opacity-50" />
                            </div>
                            <div className="flex items-center justify-between text-xs text-blue-600 mt-2">
                                <span>{siteStats.totalSites} sites</span>
                                <span>{siteStats.activeSites} actifs</span>
                            </div>
                        </div>

                        <div className="bg-green-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-green-700 mb-1">En ligne maintenant</p>
                                    <p className="text-2xl font-bold text-green-900">
                                        {siteStats.onlineNow}
                                    </p>
                                </div>
                                <Globe className="h-8 w-8 text-green-600 opacity-50" />
                            </div>
                            <div className="flex items-center justify-between text-xs text-green-600 mt-2">
                                <span>Pic: {formatNumber(siteStats.peakVisitors)}</span>
                                <span className="flex items-center">
                                    <div className="h-2 w-2 rounded-full bg-green-500 mr-1"></div>
                                    Live
                                </span>
                            </div>
                        </div>

                        <div className="bg-purple-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-purple-700 mb-1">Visites aujourd'hui</p>
                                    <p className="text-2xl font-bold text-purple-900">
                                        {formatNumber(siteStats.todayVisits)}
                                    </p>
                                </div>
                                <Calendar className="h-8 w-8 text-purple-600 opacity-50" />
                            </div>
                            <div className="flex items-center text-xs text-purple-600 mt-2">
                                <span className="flex items-center">
                                    {siteStats.isVisitPositive ? (
                                        <>
                                            <TrendingUp className="h-3 w-3 mr-1" />
                                            +{Math.abs(siteStats.visitChange)}%
                                        </>
                                    ) : (
                                        <>
                                            <TrendingDown className="h-3 w-3 mr-1" />
                                            {siteStats.visitChange}%
                                        </>
                                    )}
                                    <span className="ml-1">vs hier</span>
                                </span>
                            </div>
                        </div>

                        <div className="bg-orange-50 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-orange-700 mb-1">Performance</p>
                                    <p className="text-2xl font-bold text-orange-900">
                                        {siteStats.avgUptime.toFixed(1)}%
                                    </p>
                                </div>
                                <TrendingUp className="h-8 w-8 text-orange-600 opacity-50" />
                            </div>
                            <div className="flex items-center justify-between text-xs text-orange-600 mt-2">
                                <span>Uptime moyen</span>
                                <span>{siteStats.avgResponseTime > 0 ? `${siteStats.avgResponseTime}ms` : 'N/A'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Statistiques supplémentaires */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Taux de disponibilité</p>
                                <p className="text-sm font-semibold text-gray-900">
                                    {siteStats.avgUptime.toFixed(1)}%
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Temps de réponse moyen</p>
                                <p className="text-sm font-semibold text-gray-900">
                                    {siteStats.avgResponseTime}ms
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Sites en ligne</p>
                                <p className="text-sm font-semibold text-gray-900">
                                    {siteStats.onlineSites}/{siteStats.totalSites}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Répartition par site */}
                <div className="bg-white rounded-xl shadow p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <PieChart className="h-5 w-5 mr-2" />
                            Répartition du trafic
                        </h3>
                        <button
                            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
                            onClick={() => {
                                // Exporter les données
                                const dataStr = JSON.stringify(siteStats.pieData, null, 2);
                                const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                                const link = document.createElement('a');
                                link.setAttribute('href', dataUri);
                                link.setAttribute('download', `trafic-sites-${new Date().toISOString().split('T')[0]}.json`);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                        >
                            <Download className="h-4 w-4 mr-1" />
                            Exporter
                        </button>
                    </div>

                    {siteStats.pieData.length > 0 ? (
                        <>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RechartsPieChart>
                                        <Pie
                                            data={siteStats.pieData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {siteStats.pieData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={entry.color || COLORS[index % COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value) => [formatNumber(value), 'Visites']}
                                            labelFormatter={(label) => `Site: ${label}`}
                                        />
                                        <Legend />
                                    </RechartsPieChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Top sites */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Sites les plus visités</h4>
                                <div className="space-y-3">
                                    {siteStats.topSites.map((site, index) => (
                                        <div key={site.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                                            <div className="flex items-center flex-1 min-w-0">
                                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-xs"
                                                     style={{ backgroundColor: COLORS[index % COLORS.length] }}>
                                                    {index + 1}
                                                </div>
                                                <div className="ml-3 flex-1 min-w-0">
                                                    <div className="flex items-center">
                                                        <span className="text-sm font-medium text-gray-900 truncate">
                                                            {site.name}
                                                        </span>
                                                        <div className="flex items-center ml-2 space-x-1">
                                                            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                                                                site.status === 'active' ? 'bg-green-100 text-green-800' :
                                                                    site.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                                                        'bg-red-100 text-red-800'
                                                            }`}>
                                                                {site.status}
                                                            </span>
                                                            <span className={`h-2 w-2 rounded-full ${
                                                                site.state === true || site.state === 'online' ? 'bg-green-500' : 'bg-red-500'
                                                            }`} />
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center text-xs text-gray-500 mt-1">
                                                        <span className="truncate">{site.url?.replace(/^https?:\/\//, '')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right ml-4">
                                                <p className="text-sm font-medium text-gray-900">
                                                    {formatNumber(site.visits)}
                                                </p>
                                                <div className="flex items-center justify-end text-xs text-gray-500">
                                                    <span className="mr-2">{site.today} aujourd'hui</span>
                                                    <span className="flex items-center">
                                                        <div className={`h-2 w-2 rounded-full ${site.online > 0 ? 'bg-green-500' : 'bg-gray-300'} mr-1`}></div>
                                                        {site.online}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="h-64 flex flex-col items-center justify-center text-gray-500">
                            <PieChart className="h-12 w-12 mb-4 opacity-50" />
                            <p className="text-lg font-medium mb-2">Aucune donnée de trafic</p>
                            <p className="text-sm text-center">Les données apparaîtront avec les visites sur vos sites</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
